/**
 * Check-in document API slice (RTK). Same pattern as workingDocumentSlice / objectsSlice.
 * Thunk only: get token, call API, return or reject. Slice handles pending/fulfilled/rejected.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import UserAuth from "../../../auth/userAuth";
import AuthTeams from "../../../auth/AuthTeams";
import { checkinAPI } from "../../api/checkinAPI";
import {
  sanitizeFileName,
  getDocClassCodeOnly,
} from "../../../functions/helperMethod";
import { isTokenRefreshFailed } from "../../../functions/reduxMethod";
import { logger } from "../../../utils/logger";
import { REFRESH_TOKEN_ACTION } from "../../constants/metaData";

const userAuth = new UserAuth();

function resolveDocFormat(providedFormat, fileName, formatRequired) {
  if (providedFormat != null && String(providedFormat).trim() !== "") {
    return String(providedFormat).trim();
  }
  if (formatRequired !== true) return "";
  if (typeof fileName !== "string") return "";
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot < 0 || lastDot === fileName.length - 1) return "";
  return fileName
    .substring(lastDot + 1)
    .trim()
    .toUpperCase();
}

export const checkinDocumentsBulkThunk = createAsyncThunk(
  "checkinApi/checkinDocumentsBulk",
  async ({ documents }, { rejectWithValue }) => {
    const list = Array.isArray(documents) ? documents : [];
    logger.info(
      "checkinDocumentsBulkThunk start",
      { count: list.length },
      "REDUX",
    );
    const tokenResponse = await userAuth.getUserToken(true);
    const tokenOneDrive = tokenResponse ? tokenResponse.accessToken : null;
    if (!tokenOneDrive) {
      return rejectWithValue({
        message: "No access token",
        code: "UNAUTHORIZED",
      });
    }
    if (list.length === 0) {
      return rejectWithValue({ message: "No documents to check in." });
    }

    try {
      const res = await checkinAPI.checkinDocumentsBulk(tokenOneDrive, list);
      if (res.status !== 200 && res.status !== 201) {
        return rejectWithValue({ status: res.status, data: res.data });
      }
      logger.info("checkinDocumentsBulkThunk success", null, "REDUX");
      return { data: res.data, status: res.status };
    } catch (error) {
      logger.error("checkinDocumentsBulkThunk failed", error, "REDUX");
      return rejectWithValue({
        message: error?.message ?? "Request failed",
        code: error?.response?.status,
        data: error?.response?.data,
      });
    }
  },
);

export const checkinDocumentThunk = createAsyncThunk(
  "checkinApi/checkinDocument",
  async (
    {
      docTitle,
      docClass,
      docFormat,
      formatRequired,
      uploadFiles,
      fileUrl,
      createViewCopy,
      selectedObjects,
      uploadViewCopyCount,
      viewRequired,
      docReleaseStatus,
      oneDriveDeleteRequired,
      tokenRefreshPreAction = {
        retry: 0,
        actionType: REFRESH_TOKEN_ACTION.checkinNew,
        refreshResponse: "success",
      },
    },
    { rejectWithValue },
  ) => {
    const docClassCode = getDocClassCodeOnly(docClass ?? "");
    logger.info(
      "checkinDocumentThunk start",
      { docClass: docClassCode, docTitle },
      "REDUX",
    );
    const tokenResponse = await userAuth.getUserToken(true);
    const tokenOneDrive = tokenResponse ? tokenResponse.accessToken : null;
    if (!tokenOneDrive) {
      return rejectWithValue({
        message: "No access token",
        code: "UNAUTHORIZED",
      });
    }
    if (isTokenRefreshFailed(tokenRefreshPreAction)) {
      return rejectWithValue({ message: "Token refresh failed" });
    }

    const selectedList = Array.isArray(selectedObjects) ? selectedObjects : [];
    const uploadFilesList = Array.isArray(uploadFiles) ? uploadFiles : [];
    const objConData = selectedList.map((item) => ({
      luName: item.luName,
      keyRef: item.keyRef,
      updateRevision: "",
      updateRevisionCode: "",
      KeepLastDocRev: "LatestRevision",
      SurveyLockedFlag: "Unlocked",
      SurveyLockedFlagDb: "0",
      KeepLastDocRevDb: "L",
    }));

    const webUser = new AuthTeams().getEmail();
    const status =
      docReleaseStatus === false
        ? ""
        : uploadViewCopyCount > 0
          ? ""
          : docReleaseStatus;

    let lastResult = null;
    try {
      for (let i = 0; i < uploadFilesList.length; i++) {
        const uploadFile = uploadFilesList[i];
        const resolvedDocFormat = resolveDocFormat(
          docFormat,
          uploadFile?.name,
          formatRequired,
        );
        const fileData =
          typeof uploadFile?.base64str === "string" ? uploadFile.base64str : "";
        const shouldUseFileUrl =
          typeof fileUrl === "string" &&
          fileUrl.trim() !== "" &&
          fileData.trim() === "";
        const docInfo = {
          checkinViewCopy: false,
          docClass: docClassCode,
          title: docTitle,
          docFormat: resolvedDocFormat,
          fileData,
          // When check-in is initiated from OneDrive, frontend can omit base64 and send a URL instead.
          ...(shouldUseFileUrl ? { fileUrl: fileUrl.trim() } : {}),
          fileName: sanitizeFileName(uploadFile.name),
          objectConnections: objConData,
          createViewCopy,
          rollbackRevisionOnFail: viewRequired,
          webUser,
          status,
        };
        const res = await checkinAPI.checkinDocument(tokenOneDrive, docInfo);
        if (res.status !== 200) {
          return rejectWithValue({ status: res.status, data: res.data });
        }
        lastResult = res;
      }
      logger.info("checkinDocumentThunk success", null, "REDUX");
      return lastResult
        ? { data: lastResult.data, status: lastResult.status }
        : { data: null, status: 200 };
    } catch (error) {
      logger.error("checkinDocumentThunk failed", error, "REDUX");
      return rejectWithValue({
        message: error?.message ?? "Request failed",
        code: error?.response?.status,
      });
    }
  },
);

const initialState = {
  checkinLoading: false,
  checkinBulkLoading: false,
  checkinError: null,
};

const checkinApiSlice = createSlice({
  name: "checkinApi",
  initialState,
  reducers: {
    clearCheckinError: (state) => {
      state.checkinError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkinDocumentThunk.pending, (state) => {
        state.checkinLoading = true;
        state.checkinError = null;
      })
      .addCase(checkinDocumentThunk.fulfilled, (state) => {
        state.checkinLoading = false;
        state.checkinError = null;
      })
      .addCase(checkinDocumentThunk.rejected, (state, action) => {
        state.checkinLoading = false;
        state.checkinError = action.payload ?? { message: "Unknown error" };
      })
      .addCase(checkinDocumentsBulkThunk.pending, (state) => {
        state.checkinBulkLoading = true;
        state.checkinError = null;
      })
      .addCase(checkinDocumentsBulkThunk.fulfilled, (state) => {
        state.checkinBulkLoading = false;
        state.checkinError = null;
      })
      .addCase(checkinDocumentsBulkThunk.rejected, (state, action) => {
        state.checkinBulkLoading = false;
        state.checkinError = action.payload ?? { message: "Unknown error" };
      });
  },
});

export const { clearCheckinError } = checkinApiSlice.actions;
export default checkinApiSlice.reducer;
